import { merchantIcons } from '@assets/icons';
import env from '@config/env';
import GoogleMapReact from 'google-map-react';
import { memo, useEffect, useState } from 'react';

const DEFAULT_CENTER = { lat: 23.0225, lng: 72.5714 };
const DEFAULT_ZOOM = 15;

const LocationMarker = memo(() => (
  <img
    src={merchantIcons.locationPin}
    alt=""
    className="pointer-events-none absolute icon-lg -translate-x-1/2 -translate-y-full"
    aria-hidden="true"
  />
));

LocationMarker.displayName = 'LocationMarker';

const mapOptions = {
  draggable: false,
  // Preview maps are short; extra default controls (camera, fullscreen, etc.)
  // stack on the right and overlap. Keep only zoom.
  disableDefaultUI: true,
  zoomControl: true,
  clickableIcons: false,
  keyboardShortcuts: false,
  gestureHandling: 'none',
};

const BusinessMapPreview = memo(() => {
  const [center, setCenter] = useState(DEFAULT_CENTER);
  const [hasLocation, setHasLocation] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [isLocating, setIsLocating] = useState(true);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setLocationError('Location is not supported by this browser.');
      setIsLocating(false);
      return undefined;
    }

    let cancelled = false;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (cancelled) return;

        setCenter({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
        setHasLocation(true);
        setLocationError('');
        setIsLocating(false);
      },
      () => {
        if (cancelled) return;

        setLocationError('Unable to get your current location.');
        setIsLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );

    return () => {
      cancelled = true;
    };
  }, []);

  if (!env.GOOGLE_MAP_KEY) {
    return (
      <div
        className="h-[132px] w-full max-w-onboarding md:h-[165px] desktop:h-[220px]"
        role="status"
      >
        <div className="flex size-full items-center justify-center rounded border border-merchant-border bg-merchant-bg-subtle px-3 desktop:px-4 text-center md:rounded-[6px] desktop:rounded-lg">
          <p className="desktop:text-[16px] text-sm font-medium text-merchant-text-secondary">
            Google Maps API key is missing. Add GOOGLE_MAP_KEY to your env file.
          </p>
        </div>
      </div>
    );
  }

  if (isLocating) {
    return (
      <div
        className="h-[132px] w-full max-w-onboarding md:h-[165px] desktop:h-[220px]"
        aria-label="Current location map"
        role="status"
      >
        <div className="flex size-full items-center justify-center rounded border border-merchant-border bg-merchant-bg-subtle md:rounded-[6px] desktop:rounded-lg">
          <p className="desktop:text-[16px] text-sm font-medium text-merchant-text-secondary">
            Getting your location...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="h-[132px] w-full max-w-onboarding md:h-[165px] desktop:h-[220px]"
      aria-label="Current location map"
    >
      <div className="relative size-full overflow-hidden rounded border border-merchant-border md:rounded-[6px] desktop:rounded-lg">
        <GoogleMapReact
          key={`${center.lat}-${center.lng}-${hasLocation}`}
          bootstrapURLKeys={{ key: env.GOOGLE_MAP_KEY }}
          defaultCenter={center}
          center={center}
          defaultZoom={DEFAULT_ZOOM}
          options={mapOptions}
          yesIWantToUseGoogleMapApiInternals
          style={{ width: '100%', height: '100%' }}
        >
          {hasLocation ? <LocationMarker lat={center.lat} lng={center.lng} /> : null}
        </GoogleMapReact>

        {locationError ? (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 bg-[rgba(17,24,39,0.7)] px-4.5 desktop:px-3 py-1.5 desktop:py-2">
            <p className="text-center text-sm font-medium text-white">{locationError}</p>
          </div>
        ) : null}
      </div>
    </div>
  );
});

BusinessMapPreview.displayName = 'BusinessMapPreview';

export default BusinessMapPreview;
